import classNames from 'classnames';
import Image, { type StaticImageData } from 'next/image';

import ExploreFirstImage from '@/assets/CNC-rfq/explore-first.png';
import ExploreSecondImage from '@/assets/CNC-rfq/explore-second.png';
import ExploreThirdImage from '@/assets/CNC-rfq/explore-third.png';
import Button from '@/components/Button';
import Icon from '@/components/Icon';
import Text from '@/components/Text';
import { smoothScroll } from '@/utils/smoothScroll';

const spotlights: {
  image: StaticImageData;
  title: string;
  description: string;
  bullets: string[];
}[] = [
  {
    image: ExploreFirstImage,
    title: 'Your part, rendered and priced on arrival',
    description:
      'Drop in a DXF, STEP, STP, ZIP or PDF file and the engine renders it in an interactive 3D viewer, then you can rotate, zoom and inspect, with the estimated price attached from the very first second.',
    bullets: [
      'Automatic geometry & feature recognition',
      'Real-time price as the model loads',
    ],
  },
  {
    image: ExploreSecondImage,
    title: 'Change a spec, watch the price move',
    description:
      'Material, finish, tolerance and quantity live in a clean configurator. Every change recalculates unit price, volume tiers and lead time instantly - no waiting on a human estimator.',
    bullets: [
      'Aluminium, steel, titanium, anodizing & more',
      'Tiered pricing for 1 / 10 / 100+ units',
    ],
  },
  {
    image: ExploreThirdImage,
    title: 'Quote once, reorder in one click',
    description:
      'Every quote is saved to a private portal, shareable with a link and exportable as a clean, client-ready PDF. Returning customers reorder a past part in a single click.',
    bullets: [
      'Client-ready PDF quote export',
      'One-click reorder from quote history',
    ],
  },
];

const CncRfqFeatureSpotlights = () => {
  return (
    <div className="flex w-full flex-col gap-12">
      <div className="flex flex-col items-center gap-5 text-center">
        <Text variant="display-4" className="max-w-3xl text-neutral-0">
          Explore the core capabilities
        </Text>
        <Text className="max-w-[511px] text-center text-jb-neutral-600">
          From instant part rendering to live pricing and reorders, see what the
          engine handles for you.
        </Text>
        <Button
          variant="jbPrimary"
          className="text-[19px] text-neutral-0"
          iconClassName="text-[24px]"
          prefixIcon="material-symbols:play-circle-rounded"
          onClick={() => smoothScroll('#cnc-rfq-upload')}
        >
          Explore features
        </Button>
      </div>
      {spotlights.map(({ image, title, description, bullets }, i) => (
        <div
          key={title}
          className={classNames(
            'flex flex-col items-stretch overflow-hidden rounded-xl border border-neutral-0/[55%] bg-buttonCNC/10 backdrop-blur-sm md:flex-row',
            { 'md:flex-row-reverse': i % 2 === 1 }
          )}
        >
          <div className="relative aspect-[608/380] w-full shrink-0 bg-jb-primary-100 md:w-1/2">
            <Image src={image} alt="" fill className="object-cover" />
          </div>
          <div className="flex flex-1 flex-col justify-center gap-4 p-6 md:p-10">
            <Text variant="h4" className="font-semibold text-neutral-0">
              {title}
            </Text>
            <Text className="text-jb-neutral-600">{description}</Text>
            <ul className="flex flex-col gap-2.5">
              {bullets.map((bullet) => (
                <li key={bullet} className="flex items-center gap-2.5">
                  <div className="flex shrink-0 items-center justify-center rounded-full">
                    <Icon
                      icon="material-symbols:check"
                      className="text-[18px] text-jb-success-400"
                    />
                  </div>
                  <Text className="text-jb-neutral-600">{bullet}</Text>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CncRfqFeatureSpotlights;
